import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from '@headlessui/react';
import classNames from 'classnames';
import type React from 'react';
import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useRef, useState } from 'react';
import './Autocomplete.scss';
import {
  Field,
  useInputClasses,
  useLabelClasses,
} from '@flywire/headlessui-tailwindcss';
import { ReactComponent as Arrow } from 'icons/arrow.svg';

function standarizeText(text: string | number) {
  const textString = String(text);

  return textString
    .toLowerCase()
    .replace(/\s+/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

type Option = {
  label: string;
  value: string;
};

const EmptyOption: Option = { label: '', value: '' };

export type AutocompleteProps = {
  children: React.ReactNode;
  className?: string;
  error?: string | boolean;
  hint?: string;
  name?: string;
  onBlur?: (name?: string) => void;
  onChange?: (value: string) => void;
  onFocus?: (value: string) => void;
  options: Option[];
  selected?: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
};

interface ContextInterface {
  error?: string | boolean;
  name?: string;
  hasValue?: boolean;
  hint?: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  filteredOptions: Option[];
  setQuery: Dispatch<SetStateAction<string>>;
  onBlur?: (name?: string) => void;
}

const Context = createContext<ContextInterface>({
  filteredOptions: [],
  required: false,
  setQuery: () => {
    // does nothing
  },
});

function Input({
  className,
  'data-testid': dataTestid,
  placeholder,
}: {
  className?: string;
  'data-testid'?: string;
  placeholder?: string;
}) {
  const {
    disabled,
    filteredOptions,
    name,
    readOnly,
    required,
    setQuery,
    error,
  } = useContext(Context);
  const fieldClasses = useInputClasses({ floatingLabel: true });
  const labelClasses = useLabelClasses({
    floatingLabel: true,
    className: 'z-10',
  });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const displayValue = (selected: Option) =>
    (selected &&
      filteredOptions.find(option => option.value === selected.value)?.label) ||
    '';

  const label = required ? `${placeholder} *` : placeholder;

  return (
    <Field>
      <ComboboxButton
        className="hidden w-full appearance-none rounded bg-transparent"
        ref={buttonRef}
      />
      <div className="relative">
        <ComboboxInput
          id={name}
          className={classNames(fieldClasses, 'cursor-pointer', className)}
          data-testid={`input-${dataTestid}`}
          displayValue={displayValue}
          onChange={(evt: ChangeEvent<HTMLInputElement>) =>
            setQuery(evt.target.value)
          }
          onClick={() => buttonRef.current && buttonRef.current.click()}
          required={required}
          autoComplete="off"
          aria-required={required}
          readOnly={readOnly}
          aria-readonly={readOnly}
          aria-disabled={disabled}
          aria-autocomplete="list"
          aria-controls={`${name}-options`}
          aria-labelledby={`${name}-label`}
          aria-invalid={!!error}
          placeholder={label}
        />
        <Label
          className={labelClasses}
          onClick={() => buttonRef.current && buttonRef.current.click()}
        >
          {label}
        </Label>
        <span className="pointer-events-none absolute inset-y-0 flex items-center pr-2 ltr:right-2 rtl:left-2 z-20">
          <Arrow
            aria-hidden="true"
            className={classNames(
              'text-gray-400 transition-all',
              'rotate-0 group-data-[headlessui-state=open]:rotate-180',
            )}
          />
        </span>
      </div>
    </Field>
  );
}

function Options({ className }: { className?: string }) {
  const { filteredOptions } = useContext(Context);

  return (
    <ComboboxOptions
      transition
      className={classNames(
        'ps-[0] text-md absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-md bg-white py-0 shadow-lg ring-gray-500 disabled:ring-gray-200',
        'transition duration-100 ease-in data-[closed]:opacity-0',
        filteredOptions.length === 0 ? 'ring-0' : 'ring-1',
        className,
      )}
    >
      {filteredOptions.map((option: Option) => (
        <OptionItem option={option} key={`${option.value}-${option.label}`} />
      ))}
    </ComboboxOptions>
  );
}

function OptionItem({
  className,
  option,
}: {
  className?: string;
  option: Option;
}) {
  const { name } = useContext(Context);

  return (
    <ComboboxOption
      className={({ focus }) =>
        classNames(
          'min-h-12 p-4 text-sm text-gray-950',
          { 'bg-blue-600 text-white': focus },
          className,
        )
      }
      data-testid={`${name}-${option.value}`}
      value={option}
    >
      {option.label}
    </ComboboxOption>
  );
}

function AutocompleteError({
  children,
  className,
  ...rest
}: {
  children: ReactNode;
  className?: string;
}) {
  const { name } = useContext(Context);

  return (
    <Field.Error
      as="span"
      id={`${name}-error`}
      className={classNames('mt-1', className)}
      {...rest}
    >
      {children}
    </Field.Error>
  );
}

function Hint(props: { children: ReactNode; className?: string }) {
  const { name } = useContext(Context);

  return <Field.Hint {...props} id={`${name}-description`} />;
}

function Autocomplete({
  children,
  className,
  error,
  hint,
  name,
  onBlur,
  onChange,
  onFocus,
  options = [],
  selected,
  required,
  readOnly,
  disabled,
  'data-testid': dataTestid,
}: AutocompleteProps) {
  const [query, setQuery] = useState('');
  const justChangedRef = useRef(false);
  const filteredOptions =
    query === ''
      ? options
      : options.filter(option =>
          standarizeText(option.label).includes(standarizeText(query)),
        );
  const selectedOption =
    options.find(
      option => option.value.toLowerCase() === selected?.toLowerCase(),
    ) || EmptyOption;

  return (
    <Context.Provider
      value={{
        name,
        error,
        hasValue: !!selected,
        hint,
        required,
        readOnly,
        disabled,
        setQuery,
        filteredOptions,
        onBlur,
      }}
    >
      <Combobox
        as="div"
        name={name}
        data-testid={`autocomplete-${dataTestid}`}
        onChange={(option: Option | null) => {
          if (option) {
            justChangedRef.current = true;
            onChange?.(option.value);
          }
          setQuery('');
        }}
        onClose={() => {
          setQuery('');
          if (justChangedRef.current) {
            justChangedRef.current = false;
            return;
          }
          onBlur?.(name);
        }}
        onFocus={() => name && onFocus?.(name)}
        value={selectedOption}
        disabled={readOnly || disabled}
        className={classNames('group', className)}
      >
        {children}
      </Combobox>
    </Context.Provider>
  );
}

Autocomplete.Input = Input;
Autocomplete.Option = OptionItem;
Autocomplete.Options = Options;
Autocomplete.Error = AutocompleteError;
Autocomplete.Hint = Hint;

export { Autocomplete };
